/**
 * screen-recorder/history.js — persistent recordings library.
 *
 * Pattern lifted from interview-studio/recorder.js (listSessions /
 * loadSession / deleteSession) but simpler: screen-recorder writes one
 * OPFS file per recording, so we don't need IDB chunk-by-chunk storage.
 * We only use IDB for the per-recording METADATA sidecar (timestamp,
 * duration, size, mime, audio-missing flags) since OPFS files don't
 * have inherent metadata beyond name + bytes.
 *
 * Public surface (window.RecordingsHistory):
 *
 *   list()                  -> Promise<Array<RecordingMeta>>  sorted newest-first
 *   get(fileName)           -> Promise<File>                  reads OPFS bytes as File
 *   save(meta)              -> Promise<void>                  IDB upsert
 *   remove(fileName)        -> Promise<void>                  delete OPFS + IDB
 *   removeAll()             -> Promise<void>                  wipe history
 *   totalSizeBytes()        -> Promise<number>                sum of all
 *   quotaInfo()             -> Promise<{used, quota, available, usagePct}>
 *   autoEvictIfNeeded(thr)  -> Promise<{evicted, freed}>      LRU evict if quota>thr%
 *   downloadOne(fileName)   -> Promise<{ok,size}>             one take through __downloadFile
 *   downloadAll(name, opts) -> Promise<{via,written}>         the file / one archive / a folder
 *
 *   RecordingMeta = {
 *       fileName: '_screen-recorder_1700000000_xxx.mp4',
 *       createdAt: 1700000000,  // ms
 *       durMs: 123456,
 *       sizeBytes: 1234567,
 *       mime: 'video/mp4',
 *       ext: 'mp4',
 *       audioMissing: false,    // optional flags
 *       audioDroppedMid: false,
 *       quotaExceededMid: false,
 *       label: null             // optional user-provided name (future feature)
 *   }
 *
 * Lifecycle: ScreenRecorder.stop() calls RecordingsHistory.save(meta) on
 * successful finalize. sweepStaleOpfsRecordings in recorder.js consults
 * IDB before deleting any OPFS file — if it's in history, keep it.
 *
 * Eviction: when OPFS quota crosses 80%, autoEvictIfNeeded() removes
 * oldest recordings until usage drops below 70% (10% hysteresis to
 * prevent thrashing). User-initiated deletes take priority.
 */
(function (w) {
'use strict';
if (w.RecordingsHistory) return;

var DB_NAME = 'timbrica-screen-recorder-history';
var DB_VERSION = 1;
var STORE = 'recordings';
var FILENAME_PREFIX = '_screen-recorder_';
var EVICT_TRIGGER_PCT = 80;
var EVICT_TARGET_PCT = 70;

// ─── IndexedDB ───────────────────────────────────────────────────────
var _db = null;
function openDb() {
    if (_db) return Promise.resolve(_db);
    return new Promise(function (resolve, reject) {
        var req = indexedDB.open(DB_NAME, DB_VERSION);
        req.onupgradeneeded = function (e) {
            var db = e.target.result;
            if (!db.objectStoreNames.contains(STORE)) {
                var store = db.createObjectStore(STORE, { keyPath: 'fileName' });
                store.createIndex('byCreatedAt', 'createdAt', { unique: false });
            }
        };
        req.onsuccess = function () { _db = req.result; resolve(_db); };
        req.onerror = function () { reject(req.error); };
    });
}

function idbGetAll() {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readonly');
            var store = tx.objectStore(STORE);
            var req = store.getAll();
            req.onsuccess = function () { resolve(req.result || []); };
            req.onerror = function () { reject(req.error); };
        });
    }).catch(function () { return []; });
}
function idbGet(fileName) {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readonly');
            var req = tx.objectStore(STORE).get(fileName);
            req.onsuccess = function () { resolve(req.result || null); };
            req.onerror = function () { reject(req.error); };
        });
    }).catch(function () { return null; });
}
function idbPut(meta) {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readwrite');
            var req = tx.objectStore(STORE).put(meta);
            req.onsuccess = function () { resolve(); };
            req.onerror = function () { reject(req.error); };
        });
    });
}
// Insert only when the row is not there yet. Adoption writes what it could
// infer about a file; a finalize in another tab writes what it KNOWS — the
// finalize row must never be replaced by an adoption that raced it.
function idbAdd(meta) {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readwrite');
            var req = tx.objectStore(STORE).add(meta);
            req.onsuccess = function () { resolve(true); };
            req.onerror = function (e) {
                if (req.error && req.error.name === 'ConstraintError') {
                    try { e.preventDefault(); } catch (_) {}
                    resolve(false);
                } else reject(req.error);
            };
        });
    });
}
// Read-check-write in one transaction: the patch lands only if the row still
// looks the way the caller saw it (IndexedDB serialises readwrite transactions
// on a store, so nobody writes between the get and the put).
function idbUpdateIf(fileName, pred, patch) {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readwrite');
            var store = tx.objectStore(STORE);
            var g = store.get(fileName);
            g.onsuccess = function () {
                var row = g.result;
                if (!row || !pred(row)) { resolve(null); return; }
                var next = Object.assign({}, row, patch);
                var p = store.put(next);
                p.onsuccess = function () { resolve(next); };
                p.onerror = function () { reject(p.error); };
            };
            g.onerror = function () { reject(g.error); };
        });
    });
}
function idbDelete(fileName) {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readwrite');
            var req = tx.objectStore(STORE).delete(fileName);
            req.onsuccess = function () { resolve(); };
            req.onerror = function () { reject(req.error); };
        });
    });
}
function idbClear() {
    return openDb().then(function (db) {
        return new Promise(function (resolve, reject) {
            var tx = db.transaction(STORE, 'readwrite');
            var req = tx.objectStore(STORE).clear();
            req.onsuccess = function () { resolve(); };
            req.onerror = function () { reject(req.error); };
        });
    });
}

// ─── OPFS helpers ────────────────────────────────────────────────────
function opfsRoot() {
    if (typeof navigator === 'undefined' || !navigator.storage ||
        typeof navigator.storage.getDirectory !== 'function') {
        return Promise.reject(new Error('opfs-unavailable'));
    }
    return navigator.storage.getDirectory();
}

// MP4 integrity probe for adopted orphans. Our MP4s are written with
// fastStart:false — ftyp, then mdat streams progressively, moov LAST at
// finalize. A recording interrupted by a crash/reload therefore has no
// (or a truncated) moov and won't play. Walk the top-level boxes with two
// tiny slice-reads: a finalized file reaches a 'moov' box; a crashed one
// has a stale/zero mdat size so the walk runs off the end. Used to mark
// adopted entries `interrupted` so the history UI is honest about them.
// Three shapes of MP4 live in this directory now, and the FIRST two boxes tell
// them apart without walking a long file:
//   ftyp · free(ex-moov) · [free · mdat]… · moov   — stream lane, finalized:
//        the init moov was renamed to `free` only AFTER the flat moov was
//        appended, so this prefix proves the file is flat and complete;
//   ftyp · moov(+mvex) · [moof · mdat]…             — stream lane, interrupted
//        (or a finalize whose flatten failed): playable as fMP4 by some
//        players, but with no sample tables — recover it (Mp4Flatten);
//   ftyp · mdat · moov  /  ftyp · moov · mdat         — the older lanes: walk.
async function mp4Inspect(file) {
    var out = { valid: false, fragmented: false, flat: false };
    try {
        var size = file.size;
        if (size < 16) return out;
        var h = new DataView(await file.slice(0, 16).arrayBuffer());
        var ftypSize = h.getUint32(0);
        if (String.fromCharCode(h.getUint8(4), h.getUint8(5), h.getUint8(6), h.getUint8(7)) !== 'ftyp' || ftypSize < 8 || ftypSize + 16 > size) return out;
        var h2 = new DataView(await file.slice(ftypSize, ftypSize + 16).arrayBuffer());
        var b2size = h2.getUint32(0), b2type = String.fromCharCode(h2.getUint8(4), h2.getUint8(5), h2.getUint8(6), h2.getUint8(7));
        var child = String.fromCharCode(h2.getUint8(12), h2.getUint8(13), h2.getUint8(14), h2.getUint8(15));
        if (b2type === 'free' && child === 'mvhd') { out.valid = true; out.flat = true; return out; }
        if (b2type === 'moov' && b2size >= 8 && b2size < 8 * 1048576) {
            var moov = new Uint8Array(await file.slice(ftypSize, ftypSize + b2size).arrayBuffer());
            // scan the moov's children for mvex
            var o = 8;
            while (o + 8 <= moov.length) {
                var cs = ((moov[o] << 24) >>> 0) + (moov[o + 1] << 16) + (moov[o + 2] << 8) + moov[o + 3];
                var ct = String.fromCharCode(moov[o + 4], moov[o + 5], moov[o + 6], moov[o + 7]);
                if (ct === 'mvex') { out.valid = true; out.fragmented = true; return out; }
                if (cs < 8) break;
                o += cs;
            }
        }
    } catch (_) {}
    out.valid = await mp4HasValidMoov(file);
    return out;
}
async function mp4HasValidMoov(file) {
    try {
        var off = 0, size = file.size, guard = 0;
        while (off + 8 <= size && guard++ < 64) {
            var head = new DataView(await file.slice(off, Math.min(off + 16, size)).arrayBuffer());
            if (head.byteLength < 8) return false;
            var boxSize = head.getUint32(0);
            var type = String.fromCharCode(head.getUint8(4), head.getUint8(5), head.getUint8(6), head.getUint8(7));
            if (type === 'moov') return true;
            if (boxSize === 1) {
                if (head.byteLength < 16) return false;
                boxSize = Number(head.getBigUint64(8));
            } else if (boxSize === 0) {
                return false; // box "extends to EOF" — unfinalized mdat
            }
            if (boxSize < 8) return false;
            off += boxSize;
        }
        return false;
    } catch (_) { return false; }
}

// Movie duration of a FLAT file whose moov is its last box — what Mp4Flatten
// leaves (it appends the flat moov after the last fragment) and what the older
// fastStart:false lanes write. Read from the tail, so a long take is not walked
// box by box (a flattened hour is thousands of fragment boxes). 0 when the
// tail holds no moov ending exactly at EOF.
async function flatMovieDurationMs(file) {
    try {
        var size = file.size, win = Math.min(size, 8 * 1048576);
        if (win < 48) return 0;
        var tail = new Uint8Array(await file.slice(size - win, size).arrayBuffer());
        for (var i = tail.length - 8; i >= 4; i--) {
            if (tail[i] !== 0x6d || tail[i + 1] !== 0x6f || tail[i + 2] !== 0x6f || tail[i + 3] !== 0x76) continue;   // 'moov'
            var at = i - 4;
            var boxSize = ((tail[at] << 24) >>> 0) + (tail[at + 1] << 16) + (tail[at + 2] << 8) + tail[at + 3];
            if (at + boxSize !== tail.length || at + 48 > tail.length) continue;
            var dv = new DataView(tail.buffer, tail.byteOffset + at + 8);   // the moov's first child
            if (String.fromCharCode(dv.getUint8(4), dv.getUint8(5), dv.getUint8(6), dv.getUint8(7)) !== 'mvhd') continue;
            var v1 = dv.getUint8(8) === 1;
            var ts = dv.getUint32(v1 ? 28 : 20);
            var dur = v1 ? Number(dv.getBigUint64(32)) : dv.getUint32(24);
            return ts ? Math.round(dur / ts * 1000) : 0;
        }
    } catch (_) {}
    return 0;
}

// A recovery verdict that means «the file is open for writing elsewhere».
function isLockedVerdict(rec) {
    return !!(rec && !rec.ok && /NoModificationAllowed|locked|InvalidState/i.test((rec.reason || '') + (rec.message || '')));
}
// Once per file per page: a take that is live in another tab (or whose page is
// still tearing down) is context, not a failure — it gets its own row, never a
// `record_history_recover ok:0`.
var liveElsewhereNoted = {};
function noteLiveElsewhere(name, size) {
    if (liveElsewhereNoted[name]) return;
    liveElsewhereNoted[name] = true;
    try { if (window.__ae) window.__ae('feature_use', { slug: 'screen-recorder', f: 'record_history_live_elsewhere', sizeBytes: size || 0 }); } catch (_) {}
}

function noop() {}

// ONE reconcile at a time. At page load the blade (refreshHistory) and the
// engine (sweepStaleOpfsRecordings) call list() 20–30 ms apart, and two passes
// that adopt the same interrupted take race: the first flattens it in place,
// the second's recovery meets a flat file («no-complete-fragment»), and its
// row — built from the snapshot it read BEFORE the flatten — said
// «interrupted», no duration, the pre-flatten size, about a take that plays.
// Prod (the RU file, which holds intl too), 30 d to 26.09.2026: 299 such
// second passes in 40 sessions, every one on the same file and size as a
// successful recovery in that session. A call that arrives while a pass runs
// waits for it and gets ONE fresh pass after it (so a row saved in between is
// seen); passes never overlap.
var reconcileRunning = null, reconcileQueued = null;
function reconcile() {
    if (!reconcileRunning) {
        var p = reconcileOnce();
        reconcileRunning = p;
        var clear = function () { if (reconcileRunning === p) reconcileRunning = null; };
        p.then(clear, clear);
        return p;
    }
    if (!reconcileQueued) {
        reconcileQueued = reconcileRunning.then(noop, noop).then(function () {
            reconcileQueued = null;
            return reconcile();
        });
    }
    return reconcileQueued;
}

// A file still locked after the short retry is either a take live in another
// tab or the take of a page that just died whose worker has not let go yet
// (in Playwright the dying page held it 10+ s after a reload; prod, 30 d: 11
// sessions met such a file, and in 5 of them it never appeared in that
// session, because nothing looked again). Look again later, three times, and tell the page when a pass
// turned up something new — the recovered take then appears without a reload.
var RECHECK_MS = [5000, 15000, 45000], recheckStep = 0, recheckTimer = null, rowsVersion = 0;
function scheduleRecheck() {
    if (recheckTimer || recheckStep >= RECHECK_MS.length) return;
    recheckTimer = setTimeout(function () {
        recheckTimer = null; recheckStep++;
        var before = rowsVersion;
        reconcile().then(function () {
            if (rowsVersion === before) return;
            try { w.dispatchEvent(new CustomEvent('recordings-history:changed', { detail: { reason: 'recheck' } })); } catch (_) {}
        }, noop);
    }, RECHECK_MS[recheckStep]);
}

// Discovery + reconciliation: enumerate OPFS files matching our pattern,
// reconcile with IDB. Stale IDB rows (file gone) get removed; orphan
// OPFS files (no IDB row) get adopted with parsed-from-name metadata.
// Since tab-close no longer deletes anything (#939), orphans are
// interrupted recordings (crash / reload / accidental close) or another
// tab's still-running recording — adoption turns both into visible,
// recoverable history entries (a still-running one gets its row
// overwritten with real metadata at finalize).
async function reconcileOnce() {
    var stillLocked = false;
    var meta = await idbGetAll();
    var byName = {};
    for (var i = 0; i < meta.length; i++) byName[meta[i].fileName] = meta[i];
    var root;
    try { root = await opfsRoot(); } catch (_) { return meta; }

    var opfsNames = {};
    try {
        for await (var entry of root.values()) {
            if (entry.kind !== 'file') continue;
            if (!entry.name.lastIndexOf(FILENAME_PREFIX, 0) === 0) continue;
            if (!/^_screen-recorder_/.test(entry.name)) continue;
            // Skip Chromium's .crswap atomic-write temp files.
            if (/\.crswap$/.test(entry.name)) continue;
            opfsNames[entry.name] = true;
            var known = byName[entry.name];
            if (known && known.adopted && known.interrupted && (known.ext || 'mp4') === 'mp4') {
                // Rows the race above wrote before 27.09.2026 still say
                // «interrupted» about a take that is flat and plays: heal them
                // from the file. Only while the row still says so — a finalize
                // row written meanwhile is not touched.
                try {
                    var hf = await entry.getFile();
                    var hi = await mp4Inspect(hf);
                    if (hi.valid && !hi.fragmented) {
                        var hd = known.durMs || await flatMovieDurationMs(hf);
                        var healed = await idbUpdateIf(entry.name, function (r) { return r.adopted && r.interrupted; },
                            { interrupted: false, recovered: true, durMs: hd, sizeBytes: hf.size });
                        if (healed) {
                            byName[entry.name] = healed; rowsVersion++;
                            try { if (window.__ae) window.__ae('feature_use', { slug: 'screen-recorder', f: 'record_history_row_healed', durMs: hd, sizeBytes: hf.size }); } catch (_) {}
                        }
                    }
                } catch (_) {}
            }
            if (!known) {
                // Adopt orphan — parse what we can from the filename.
                var m = entry.name.match(/^_screen-recorder_(\d+)_/);
                var ts = m ? parseInt(m[1], 10) : Date.now();
                var ext = (entry.name.split('.').pop() || 'mp4').toLowerCase();
                try {
                    var f = await entry.getFile();
                    // Empty / sub-header stub: either an abandoned
                    // createWritable() target whose swap was discarded
                    // (mid-recording crash/reload — the OPFS atomic-write
                    // model commits only on close(), so the real file stays
                    // 0 B) OR the still-0-B target of a recording LIVE in
                    // another tab (writes are in the uncommitted swap). Both
                    // must be SKIPPED, not adopted: adopting shows a fake
                    // 0 B "recording" (#939 phantom) and, worse, deleting it
                    // here would destroy a concurrent tab's live target. 0 B
                    // costs no quota; the age-gated 5-min sweep reclaims true
                    // garbage. 1 KB floor: a valid finalized file is tens of
                    // KB minimum (ftyp+moov+mdat).
                    if (!f || f.size < 1024) continue;
                    // The take THIS page is recording right now is an orphan
                    // too (its row arrives at finalize) and its lock is ours:
                    // never probe it. The recorder announces the name.
                    if (window.__srLiveOpfsName && entry.name === window.__srLiveOpfsName) continue;
                    // Interrupted-MP4 detection: no valid moov = crashed
                    // before finalize = won't play. WebM partials are
                    // cluster-based and generally playable up to the cut,
                    // so they adopt clean.
                    var interrupted = false, recovered = false, durMs = 0;
                    if (ext === 'mp4') {
                        var insp = await mp4Inspect(f);
                        interrupted = !insp.valid;
                        // The stream lane writes FRAGMENTED MP4 straight into
                        // the file, so an interrupted take is a run of complete
                        // fragments behind an init moov that has no sample
                        // tables. Mp4Flatten (in the worker) turns it into a
                        // flat, playable MP4 in place — the same routine
                        // finalize uses. A take still LIVE in another tab holds
                        // the file's lock, so recovery refuses it and the entry
                        // is skipped, exactly as the 0-byte stub was before.
                        if ((interrupted || insp.fragmented) && window.SrOpfsStream && window.SrOpfsStream.supported()) {
                            var rec = null;
                            try { rec = await window.SrOpfsStream.recover(entry.name); } catch (_) { rec = null; }
                            // A locked file is one of two things: a take live in
                            // another tab (stays locked), or the take the page
                            // that just died was writing — its worker releases
                            // the handle within a second of the navigation, and
                            // the first probe of the new page loses that race.
                            // Prod, RU, 30 d to 2026-09-16: 340 «failed»
                            // recoveries in 49 sessions, every one followed by a
                            // successful one from the second caller moments
                            // later. One short retry settles most of them; a file
                            // still locked after it is skipped without a failure
                            // row and looked at again later (scheduleRecheck) —
                            // a dying page can hold it longer than the retry.
                            if (isLockedVerdict(rec)) {
                                await new Promise(function (r) { setTimeout(r, 1200); });
                                try { rec = await window.SrOpfsStream.recover(entry.name); } catch (_) { rec = null; }
                            }
                            if (isLockedVerdict(rec)) {
                                noteLiveElsewhere(entry.name, f.size);
                                stillLocked = true;
                                continue;   // somebody is still writing it — looked at again later
                            }
                            // The row describes the file as it is NOW, after
                            // this recovery or somebody else's: a pass that meets
                            // a take another pass (another tab, a page that died)
                            // already flattened gets «no-complete-fragment», and
                            // the snapshot it read before is stale.
                            var fNow = f;
                            try { fNow = await entry.getFile(); } catch (_) {}
                            var now = await mp4Inspect(fNow);
                            var flatNow = now.valid && !now.fragmented;
                            try { if (window.__ae) window.__ae('feature_use', { slug: 'screen-recorder', f: 'record_history_recover', ok: rec && rec.ok ? 1 : 0, reason: (rec && rec.reason) || '', fragments: (rec && rec.fragments) || 0, dropped: (rec && rec.dropped) || 0, durMs: (rec && rec.durationMs) || 0, sizeBytes: f.size, now: flatNow ? 'flat' : (now.fragmented ? 'frag' : 'bad') }); } catch (_) {}
                            f = fNow;
                            // still an fMP4 (or no moov at all) — playable by some
                            // players at best, so the row says so
                            interrupted = !flatNow;
                            recovered = flatNow;
                            if (flatNow) durMs = (rec && rec.ok && rec.durationMs) || 0;
                        }
                        // A flat take whose duration nobody reported (adopted
                        // already flat, or flattened by another pass) — read it
                        // from its moov instead of showing «–».
                        if (!durMs && !interrupted) durMs = await flatMovieDurationMs(f);
                    }
                    var adopted = {
                        fileName: entry.name,
                        createdAt: ts,
                        durMs: durMs,        // from the recovery or the flat file's moov; 0 = unknown
                        sizeBytes: f.size,
                        mime: f.type || (ext === 'webm' ? 'video/webm' : 'video/mp4'),
                        ext: ext,
                        audioMissing: false,
                        audioDroppedMid: false,
                        quotaExceededMid: false,
                        label: null,
                        adopted: true,
                        interrupted: interrupted,
                        recovered: recovered
                    };
                    if (await idbAdd(adopted)) {
                        byName[entry.name] = adopted; rowsVersion++;
                    } else {
                        var theirs = await idbGet(entry.name);   // finalized meanwhile — its row wins
                        if (theirs) byName[entry.name] = theirs;
                    }
                } catch (_) {}
            }
        }
    } catch (_) {}
    if (stillLocked) scheduleRecheck();
    else recheckStep = 0;

    // Remove IDB rows whose OPFS file vanished (manual delete, browser
    // storage eviction, etc.).
    for (var k in byName) {
        if (Object.prototype.hasOwnProperty.call(byName, k) && !opfsNames[k]) {
            try { await idbDelete(k); } catch (_) {}
            delete byName[k];
        }
    }
    return Object.values(byName);
}

// What the list on screen was last built from. «Скачать всё» picks its lane
// from this BEFORE anything is awaited: the folder dialog lives on the click's
// activation, and a reconcile can take seconds (a recovery, a locked take).
var lastRows = null;

async function list() {
    var rows = await reconcile();
    rows.sort(function (a, b) { return (b.createdAt || 0) - (a.createdAt || 0); });
    lastRows = rows;
    // Two takes or more: «Скачать всё» will need the batch door, and loading it
    // inside the click would spend the activation its folder dialog needs.
    if (rows.length > 1 && !w.__batchDeliver && w.__ensureBatchDeliver) {
        try { w.__ensureBatchDeliver().catch(noop); } catch (_) {}
    }
    return rows;
}

async function get(fileName) {
    var root = await opfsRoot();
    var fh = await root.getFileHandle(fileName, { create: false });
    return await fh.getFile();
}

// The take reached the person's disk (download, «Save as», a folder): its
// copy here becomes the first to go when room is short (autoEvictIfNeeded).
async function markDelivered(fileName) {
    if (!fileName) return false;
    try {
        await idbUpdateIf(fileName, function (r) { return !r.deliveredAt; }, { deliveredAt: Date.now() });
        return true;
    } catch (_) { return false; }
}

// What a short disk can get back right now without touching anything that is
// not already on the person's disk: delivered takes, newest included.
async function deliveredBytes() {
    try {
        var rows = await idbGetAll();
        var live = w.__srLiveOpfsName || '';
        return rows.reduce(function (n, r) { return n + (r.deliveredAt && r.fileName !== live ? (r.sizeBytes || 0) : 0); }, 0);
    } catch (_) { return 0; }
}
async function evictDelivered() {
    var rows = await idbGetAll(), freed = 0, n = 0, live = w.__srLiveOpfsName || '';
    for (var i = 0; i < rows.length; i++) {
        var r = rows[i];
        if (!r.deliveredAt || r.fileName === live) continue;
        try { await remove(r.fileName); freed += r.sizeBytes || 0; n++; } catch (_) {}
    }
    return { evicted: n, freed: freed };
}

async function save(meta) {
    if (!meta || !meta.fileName) return;
    meta.createdAt = meta.createdAt || Date.now();
    await idbPut(meta);
}

async function remove(fileName) {
    try {
        var root = await opfsRoot();
        try { await root.removeEntry(fileName); } catch (_) {}
    } catch (_) {}
    await idbDelete(fileName);
}

async function removeAll() {
    var rows = await idbGetAll();
    var root;
    try { root = await opfsRoot(); } catch (_) { root = null; }
    if (root) {
        for (var i = 0; i < rows.length; i++) {
            try { await root.removeEntry(rows[i].fileName); } catch (_) {}
        }
    }
    await idbClear();
}

async function totalSizeBytes() {
    var rows = await idbGetAll();
    var sum = 0;
    for (var i = 0; i < rows.length; i++) sum += rows[i].sizeBytes || 0;
    return sum;
}

async function quotaInfo() {
    /* The REAL grant, not estimate(): on most Chromium builds estimate() says
       usage + 10 GiB whatever is really left, so a full disk never looked full
       here (window.__storageEstimate, timbrica-core.js — support chat 647). */
    var est = window.__storageEstimate ? await window.__storageEstimate() : null;
    if (!est) {
        return { used: 0, quota: Infinity, available: Infinity, usagePct: 0 };
    }
    var quota = est.quota || 0;
    var usage = est.usage || 0;
    return {
        used: usage,
        quota: quota,
        available: Math.max(0, quota - usage),
        usagePct: quota > 0 ? (usage / quota) * 100 : 0
    };
}

// LRU eviction. When usage crosses `triggerPct`, remove oldest recordings
// until below `targetPct`. Returns count + bytes freed + `unmet` (target
// unreachable without touching protected entries).
//
// Two call modes:
//   - Reactive (after save): autoEvictIfNeeded()      → 80% → 70%, gentle
//   - Pre-flight (before start): autoEvictIfNeeded(60, 50) → aggressive,
//     ensures room for a normal recording. Closes 8f56cfd3 today-cluster:
//     OPFS was already at 95%+ at start, autoEvictIfNeeded never ran
//     because it only kicked in AFTER successful save (which never
//     succeeded because close() ran out of quota).
//
// PROTECTED ENTRIES (feedback #939 "запись не нашёл… полная лажа, развод"):
// the NEWEST recording is NEVER evicted, nor is anything in
// opts.protectNames (the just-finalized file, an in-progress file). On a
// quota-starved machine (вовыч: 317 MB total quota because his disk was
// full) a single normal recording is >80% usage by itself, so the old
// percent-chasing loop deleted the file the user had JUST recorded —
// twice in one session, seconds after finalize, before he could download.
// A library that destroys the user's latest recording to "free space" has
// its priorities upside down: staying over targetPct is the lesser evil,
// report it via `unmet` and let the caller warn instead.
async function autoEvictIfNeeded(triggerPct, targetPct, opts) {
    triggerPct = triggerPct || EVICT_TRIGGER_PCT;
    targetPct = targetPct || EVICT_TARGET_PCT;
    opts = opts || {};
    if (targetPct >= triggerPct) targetPct = triggerPct - 10; // safety
    var q = await quotaInfo();
    if (q.usagePct < triggerPct) return { evicted: 0, freed: 0, unmet: false };
    var rows = await idbGetAll();
    rows.sort(function (a, b) { return (a.createdAt || 0) - (b.createdAt || 0) ; }); // oldest first
    var protectedNames = {};
    var pn = opts.protectNames || [];
    for (var p = 0; p < pn.length; p++) protectedNames[pn[p]] = true;
    // The newest take is protected (#939: the library deleted the file the
    // person had just recorded, before they could download it) — UNLESS it
    // has already been delivered: then it is on their disk, and its copy here
    // is a duplicate that steals the next take's room. Отзыв #2012
    // (24.10.2026): a 300 MB grant, the 30-minute take downloaded AND kept
    // here, the next take began with 148 MB and stopped after 8 minutes.
    var newest = rows.length ? rows[rows.length - 1] : null;
    if (newest && !newest.deliveredAt) protectedNames[newest.fileName] = true;
    // Delivered takes go first (oldest first among them), then the rest.
    rows.sort(function (a, b) {
        var da = a.deliveredAt ? 0 : 1, db = b.deliveredAt ? 0 : 1;
        return da !== db ? da - db : (a.createdAt || 0) - (b.createdAt || 0);
    });
    var freed = 0, evicted = 0;
    for (var i = 0; i < rows.length; i++) {
        if ((q.usagePct - (freed / q.quota) * 100) < targetPct) break;
        var r = rows[i];
        if (protectedNames[r.fileName]) continue;
        try { await remove(r.fileName); } catch (_) {}
        freed += r.sizeBytes || 0;
        evicted++;
    }
    var unmet = (q.usagePct - (freed / q.quota) * 100) >= targetPct;
    return { evicted: evicted, freed: freed, unmet: unmet };
}

// The name a take leaves the page under.
function downloadName(fileName) {
    return String(fileName).replace(/^_screen-recorder_\d+_/, 'screen-recording_').replace(/_/, '-');
}

// One take, through the download door (window.__downloadFile): the blob URL
// outlives the transfer, a disk too full for the file is said BEFORE the wait,
// and the person is told where the file went. The row's own anchor did none of
// that and revoked its URL after 60 s — a 20 GB take copies for minutes.
async function downloadOne(fileName) {
    var f = await get(fileName);
    if (typeof w.__downloadFile !== 'function') throw new Error('download-door-unavailable');
    var ok = await w.__downloadFile(f, downloadName(fileName));
    if (ok !== false) await markDelivered(fileName);
    return { ok: ok !== false, size: f.size };
}

// «Скачать всё» — three answers, picked by what the takes weigh:
//   one take              the file itself (an archive of one video is only a
//                         second copy to unpack);
//   takes fit one archive one archive, as the button always answered;
//   takes do not fit      a folder — every take is streamed to disk, and there
//                         is no ceiling to hit.
// Until 07.10.2026 the button was archive-only, and an archive stops near
// 1.4 GiB (__batchDeliver.archiveCeilingBytes): a single 13 GB take was answered
// «не помещаются в один архив… сохраните их в папку» by a button that had no
// folder to offer — seven times in one session (support chat 659). Names stay
// distinct on every lane: that is the batch door's job (uniqueName).
async function downloadAll(suggestedName, opts) {
    opts = opts || {};
    var known = lastRows || await list();
    if (!known.length) throw new Error('history-empty');
    if (known.length === 1) {
        var one = await downloadOne(known[0].fileName);
        return { via: one.ok ? 'file' : 'refused', written: one.ok ? 1 : 0 };
    }
    var BD = w.__batchDeliver || (w.__ensureBatchDeliver ? await w.__ensureBatchDeliver() : null);
    if (!BD) throw new Error('batch-deliver-unavailable');
    var total = 0;
    for (var i = 0; i < known.length; i++) total += known[i].sizeBytes || 0;
    // First await of the lane = the folder dialog, when a folder is the answer.
    var batch = await BD.openBatch({
        slug: 'screen-recorder',
        zipName: (suggestedName || 'screen-recordings') + '.zip',
        total: known.length,
        archiveOnly: BD.fitsArchive(total),
        onProgress: opts.onProgress
    });
    var landed = [];
    try {
        for (var j = 0; j < known.length; j++) {
            var r = known[j], f;
            try { f = await get(r.fileName); } catch (_) { continue; }   // deleted meanwhile
            var label = (r.label || 'recording') + '_' + new Date(r.createdAt).toISOString().slice(0, 19).replace(/[:T]/g, '-') + '.' + r.ext;
            await batch.write(label, f);
            landed.push(r.fileName);
        }
    } catch (_) {
        // a write that failed (disk full, folder gone): finish() reports what landed
    }
    var res = await batch.finish();
    // Delivered takes become the first to go when room is short.
    if (res && res.via !== 'refused' && (res.written || 0) > 0) {
        for (var k = 0; k < landed.length && k < res.written; k++) await markDelivered(landed[k]);
    }
    return res;
}

w.RecordingsHistory = {
    list: list,
    get: get,
    save: save,
    remove: remove,
    removeAll: removeAll,
    totalSizeBytes: totalSizeBytes,
    quotaInfo: quotaInfo,
    autoEvictIfNeeded: autoEvictIfNeeded,
    markDelivered: markDelivered,
    deliveredBytes: deliveredBytes,
    evictDelivered: evictDelivered,
    downloadOne: downloadOne,
    downloadAll: downloadAll
};

})(window);
